import { QueryClient, useQueryClient } from "@tanstack/react-query";
import {
	CacheEntry,
	planResolvedAlertRemoval,
	RESOLVED_ALERT_CACHE_KEYS,
} from "../utils/resolved-alert-cache";

/**
 * Optimistic resolve, shared by every surface that resolves an alert: Home's
 * Critical alerts, the Requires attention tab and the participant's Requires
 * attention tab (KAN-2553 follow-up, Maria 2026-09-29: "when I resolve an
 * alert, it doesn't immediately disappear").
 *
 * Takes the alert out of every cached unresolved-only list, lowers the matching
 * table totals and category badges, and resolves to a function that puts every
 * touched entry back exactly as it was — call it if the resolve fails. Which
 * entries are touched is decided by the pure `planResolvedAlertRemoval`; views
 * that can show resolved tasks (the Tasks tab) are never touched.
 *
 * The server stays the source of truth: `useUpdateTask` invalidates all three
 * task keys on success, so the refetch confirms the removal. Before writing,
 * any in-flight fetch of a touched entry is cancelled (and awaited — a cancel
 * reverts the cache asynchronously), so a response that started before the
 * resolve cannot paint the row back in.
 */
export const hideResolvedAlert = async (
	queryClient: QueryClient,
	alertId: number
): Promise<() => void> => {
	const readEntries = (): CacheEntry[] =>
		queryClient
			.getQueriesData({
				predicate: (query) =>
					RESOLVED_ALERT_CACHE_KEYS.includes(query.queryKey[0] as string),
			})
			.map(([queryKey, data]) => ({ queryKey, data }));

	const toCancel = planResolvedAlertRemoval(readEntries(), alertId);
	await Promise.all(
		toCancel.map(({ queryKey }) => queryClient.cancelQueries({ queryKey, exact: true }))
	);

	const updates = planResolvedAlertRemoval(readEntries(), alertId);
	const previous = updates.map(({ queryKey }) => ({
		queryKey,
		data: queryClient.getQueryData(queryKey),
	}));
	updates.forEach(({ queryKey, data }) => queryClient.setQueryData(queryKey, data));

	return () => previous.forEach(({ queryKey, data }) => queryClient.setQueryData(queryKey, data));
};

/** `hideResolvedAlert` bound to the app's QueryClient. */
export const useHideResolvedAlert = () => {
	const queryClient = useQueryClient();
	return (alertId: number) => hideResolvedAlert(queryClient, alertId);
};
